/* =============================================================================
 * MHV Dialog — shared, theme-aware replacements for native alert/confirm/prompt
 * =============================================================================
 * Native browser alert()/confirm()/prompt() render as unstyled OS popups that
 * clash with the admin suite. This provides themed, Promise-based equivalents
 * that match the rest of the UI (dark + magenta on admin pages, light/plum on
 * the storefront — it uses the same var(--mhv-*) tokens, so it adapts).
 *
 * Loaded globally via theme.liquid (after mhv-utils.js), so every page can use:
 *   await MHV.confirm('Delete this?', { danger: true })   -> boolean
 *   await MHV.alert('Saved.')                              -> void
 *   const name = await MHV.prompt('New name?', { defaultValue: 'x' }) -> string|null
 *   MHV.toast('Network error', { type: 'error' })          -> non-blocking
 *
 * Self-contained: injects its own CSS once, creates window.MHV if missing.
 * ============================================================================= */
(function () {
  var MHV = window.MHV = window.MHV || {};
  if (MHV.__dialogReady) return;
  MHV.__dialogReady = true;

  var Z = 2147483000; // above every other overlay in the suite

  // ---------------------------------------------------------------------------
  // CSS (injected once) — all colors via theme tokens so it matches the page.
  // ---------------------------------------------------------------------------
  function injectCSS() {
    if (document.getElementById('mhv-dialog-styles')) return;
    var css =
      '.mhvd-overlay{position:fixed;inset:0;z-index:' + Z + ';display:flex;align-items:center;justify-content:center;' +
        'background:rgba(0,0,0,0.6);padding:16px;opacity:0;transition:opacity .12s ease;}' +
      '.mhvd-overlay.mhvd-in{opacity:1;}' +
      '.mhvd-card{position:relative;width:420px;max-width:100%;background:var(--mhv-bg-elevated,#1a1a1a);' +
        'border:1px solid rgba(var(--mhv-accent-rgb,255,0,194),0.35);border-radius:10px;' +
        'box-shadow:0 12px 48px rgba(0,0,0,0.55);color:var(--mhv-text-primary,#fff);' +
        'font-family:inherit;transform:translateY(6px) scale(.99);transition:transform .12s ease;}' +
      '.mhvd-overlay.mhvd-in .mhvd-card{transform:none;}' +
      '.mhvd-header{padding:16px 20px 0;font-size:14px;font-weight:700;letter-spacing:.5px;' +
        'text-transform:uppercase;color:var(--mhv-accent,#FF00C2);}' +
      '.mhvd-body{padding:12px 20px 4px;font-size:14px;line-height:1.55;color:var(--mhv-text-secondary,#ddd);' +
        'white-space:pre-wrap;word-break:break-word;}' +
      '.mhvd-input{width:100%;margin-top:12px;padding:9px 11px;font-size:14px;font-family:inherit;' +
        'background:var(--mhv-bg-input-solid,rgba(255,255,255,0.06));color:var(--mhv-text-primary,#fff);' +
        'border:1px solid rgba(var(--mhv-accent-rgb,255,0,194),0.3);border-radius:6px;box-sizing:border-box;}' +
      '.mhvd-input:focus{outline:none;border-color:rgba(var(--mhv-accent-rgb,255,0,194),0.6);}' +
      '.mhvd-footer{display:flex;justify-content:flex-end;gap:8px;padding:16px 20px 18px;flex-wrap:wrap;}' +
      '.mhvd-btn{padding:8px 18px;font-size:13px;font-weight:600;font-family:inherit;letter-spacing:.3px;' +
        'border-radius:6px;cursor:pointer;border:1px solid transparent;}' +
      '.mhvd-btn-primary{background:var(--mhv-accent,#FF00C2);color:#fff;border-color:var(--mhv-accent,#FF00C2);}' +
      '.mhvd-btn-primary:hover{background:var(--mhv-accent-hover,#e600af);}' +
      '.mhvd-btn-primary.mhvd-danger{background:var(--mhv-error,#c0392b);border-color:var(--mhv-error,#c0392b);}' +
      '.mhvd-btn-primary.mhvd-danger:hover{background:var(--mhv-error-bright,#e04545);}' +
      '.mhvd-btn-secondary{background:transparent;color:var(--mhv-text-secondary,#ccc);' +
        'border-color:rgba(var(--mhv-accent-rgb,255,0,194),0.25);}' +
      '.mhvd-btn-secondary:hover{border-color:rgba(var(--mhv-accent-rgb,255,0,194),0.5);}' +
      /* toasts */
      '.mhvd-toast-host{position:fixed;top:14px;right:14px;z-index:' + (Z + 1) + ';display:flex;flex-direction:column;gap:8px;pointer-events:none;max-width:min(380px,90vw);}' +
      '.mhvd-toast{pointer-events:auto;background:var(--mhv-bg-elevated,#1a1a1a);color:var(--mhv-text-primary,#fff);' +
        'border:1px solid rgba(var(--mhv-accent-rgb,255,0,194),0.3);border-left:4px solid var(--mhv-accent,#FF00C2);' +
        'border-radius:8px;padding:11px 14px;font-size:13px;line-height:1.45;box-shadow:0 6px 24px rgba(0,0,0,0.5);' +
        'opacity:0;transform:translateX(12px);transition:opacity .15s ease,transform .15s ease;word-break:break-word;}' +
      '.mhvd-toast.mhvd-in{opacity:1;transform:none;}' +
      '.mhvd-toast.mhvd-success{border-left-color:var(--mhv-success,#2d8a4e);}' +
      '.mhvd-toast.mhvd-error{border-left-color:var(--mhv-error,#c0392b);}' +
      '.mhvd-toast.mhvd-warning{border-left-color:var(--mhv-warning,#C4A87A);}';
    var el = document.createElement('style');
    el.id = 'mhv-dialog-styles';
    el.textContent = css;
    (document.head || document.documentElement).appendChild(el);
  }

  function whenBody(fn) {
    if (document.body) fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // Quote-safe: escapes " and ' too, so it is correct in BOTH element-text
  // (header/message/buttons) AND quoted-attribute (placeholder="…"/value="…")
  // contexts. Mirrors MHV.escapeAttr — a raw value in, entity out (no double-escape).
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ---------------------------------------------------------------------------
  // Core modal — returns a Promise. result(value) is called with the resolution.
  // ---------------------------------------------------------------------------
  function openModal(opts) {
    injectCSS();
    return new Promise(function (resolve) {
      whenBody(function () {
        var overlay = document.createElement('div');
        overlay.className = 'mhvd-overlay';
        var inputId = 'mhvd-input-' + Math.floor(performance.now ? performance.now() : 0) + '-' + (openModal._n = (openModal._n || 0) + 1);

        var html = '<div class="mhvd-card" role="dialog" aria-modal="true">';
        if (opts.title) html += '<div class="mhvd-header">' + esc(opts.title) + '</div>';
        html += '<div class="mhvd-body">' + esc(opts.message);
        if (opts.input) {
          html += '<input id="' + inputId + '" class="mhvd-input" type="' + (opts.inputType || 'text') + '" ' +
            'placeholder="' + esc(opts.placeholder || '') + '" value="' + esc(opts.defaultValue || '') + '">';
        }
        html += '</div><div class="mhvd-footer">';
        if (opts.cancel !== false) {
          html += '<button type="button" class="mhvd-btn mhvd-btn-secondary" data-act="cancel">' + esc(opts.cancelText || 'Cancel') + '</button>';
        }
        html += '<button type="button" class="mhvd-btn mhvd-btn-primary' + (opts.danger ? ' mhvd-danger' : '') + '" data-act="ok">' +
          esc(opts.okText || 'OK') + '</button></div></div>';
        overlay.innerHTML = html;
        document.body.appendChild(overlay);
        requestAnimationFrame(function () { overlay.classList.add('mhvd-in'); });

        var inputEl = opts.input ? overlay.querySelector('#' + inputId) : null;
        var done = false;
        function close(value) {
          if (done) return; done = true;
          overlay.classList.remove('mhvd-in');
          document.removeEventListener('keydown', onKey, true);
          setTimeout(function () { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); }, 140);
          resolve(value);
        }
        function onOk() { close(opts.input ? (inputEl ? inputEl.value : '') : true); }
        function onCancel() { close(opts.input ? null : false); }
        function onKey(e) {
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          else if (e.key === 'Enter' && (!opts.input || document.activeElement === inputEl)) { e.preventDefault(); onOk(); }
        }
        // Only dismiss on a backdrop click whose press STARTED on the backdrop —
        // otherwise a text-selection drag that ends on the backdrop closes it.
        var dlgDownOnOverlay = false;
        overlay.addEventListener('mousedown', function (e) { dlgDownOnOverlay = (e.target === overlay); });
        overlay.addEventListener('click', function (e) {
          var act = e.target && e.target.getAttribute && e.target.getAttribute('data-act');
          var startedOnOverlay = dlgDownOnOverlay;
          dlgDownOnOverlay = false;
          if (act === 'ok') onOk();
          else if (act === 'cancel') onCancel();
          else if (e.target === overlay && startedOnOverlay && opts.dismissOnBackdrop !== false) onCancel();
        });
        document.addEventListener('keydown', onKey, true);
        setTimeout(function () { if (inputEl) { inputEl.focus(); inputEl.select(); } else { var b = overlay.querySelector('[data-act="ok"]'); if (b) b.focus(); } }, 30);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------
  MHV.alert = function (message, opts) {
    opts = opts || {};
    return openModal({ title: opts.title || 'Notice', message: message, okText: opts.okText || 'OK', cancel: false, danger: opts.danger });
  };

  MHV.confirm = function (message, opts) {
    opts = opts || {};
    return openModal({
      title: opts.title || 'Confirm', message: message,
      okText: opts.okText || 'OK', cancelText: opts.cancelText || 'Cancel', danger: opts.danger
    });
  };

  MHV.prompt = function (message, opts) {
    opts = opts || {};
    return openModal({
      title: opts.title || 'Enter value', message: message, input: true,
      inputType: opts.inputType || 'text', defaultValue: opts.defaultValue || '', placeholder: opts.placeholder || '',
      okText: opts.okText || 'OK', cancelText: opts.cancelText || 'Cancel', danger: !!opts.danger
    });
  };

  var toastHost = null;
  function ensureToastHost() {
    injectCSS();
    if (toastHost && document.body && document.body.contains(toastHost)) return toastHost;
    toastHost = document.createElement('div');
    toastHost.className = 'mhvd-toast-host';
    (document.body || document.documentElement).appendChild(toastHost);
    return toastHost;
  }

  MHV.toast = function (message, opts) {
    opts = opts || {};
    whenBody(function () {
      var host = ensureToastHost();
      var t = document.createElement('div');
      t.className = 'mhvd-toast' + (opts.type ? ' mhvd-' + opts.type : '');
      t.textContent = String(message == null ? '' : message);
      host.appendChild(t);
      requestAnimationFrame(function () { t.classList.add('mhvd-in'); });
      var dur = opts.duration || 3600;
      setTimeout(function () {
        t.classList.remove('mhvd-in');
        setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 200);
      }, dur);
    });
  };
})();

// =============================================================================
// A DRAG IS NOT A CLICK (2026-10-14, owner: "if i swipe over text to highlight
// with a mouse, and land off the modal, it dismisses the modal … look for
// anywhere else it happens").
// -----------------------------------------------------------------------------
// Press inside a modal, drag to select some text, let go over the dark backdrop:
// the browser fires `click` on the COMMON ANCESTOR of the press and the release,
// which is the backdrop itself — so every `if (e.target === overlay) close()`
// closes the modal under the person reading it. MHV.backdropDismiss() already
// gets this right (it demands the PRESS start on the backdrop), but roughly a
// hundred modals across the theme wire their own handler, and fixing them one at
// a time is how the hundred-and-first gets missed.
//
// So this swallows, at the window in the capture phase (before any modal's own
// handler can see it), a click that only exists because of a drag:
//   • the click landed on an ANCESTOR of where the press started (not the press
//     target itself — a genuine click is delivered where it was pressed), AND
//   • the pointer travelled more than DRAG_PX, AND
//   • it was a selection or a drag over an overlay: text is selected, the press
//     began in a field, or the element clicked is position:fixed (every backdrop).
// ⚠️ All three, on purpose: a button whose icon was pressed and released a few
// pixels away on its own padding is ALSO an ancestor-click, and must still work —
// it moves less than DRAG_PX, selects nothing and is not a fixed element.
// =============================================================================
(function () {
  if (window.__mhvDragClickGuard) return;
  window.__mhvDragClickGuard = true;
  var DRAG_PX = 10;
  var down = null;
  window.addEventListener('mousedown', function (e) {
    down = (e.button === 0) ? { t: e.target, x: e.clientX, y: e.clientY } : null;
  }, true);
  window.addEventListener('click', function (e) {
    var d = down; down = null;
    if (!d || !d.t || e.button !== 0) return;
    var t = e.target;
    if (!t || t === d.t || typeof t.contains !== 'function' || !t.contains(d.t)) return;
    var moved = Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y);
    if (moved <= DRAG_PX) return;
    var sel = '';
    try { sel = String(window.getSelection ? window.getSelection() : ''); } catch (err) { sel = ''; }
    var inField = !!(d.t.closest && d.t.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]'));
    var fixed = false;
    try { fixed = window.getComputedStyle(t).position === 'fixed'; } catch (err) { fixed = false; }
    if (!sel && !inField && !fixed) return;
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
  }, true);
})();
